import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import { extractRoleFromClientId } from '@/features/live/ablyClientId';
import {
  chatModeEventReceived,
  chatModeRequestFulfilled,
  chatModeRequestStarted,
  initialEvergreenChatModeSync,
  type ReplayedChatMode,
} from '@/lib/evergreenChatMode';
import {
  applyInviteRequestEvent,
  EMPTY_PENDING_INVITES,
  reconcilePendingInvites,
  type InviteRequestEvent,
  type RoomSnapshot,
} from '@/lib/inviteRequestState';
import type {
  ChatMessageDto,
  ChatToken,
  LivekitParticipant,
  LiveSessionResourceStateDto,
  LiveState,
  ModeratorToken,
  PlaybackInfo,
  PresenterToken,
  RoleToken,
  RotatedInviteToken,
  SessionPresenceDto,
  SpeakerToken,
  StreamStartStatus,
  ViewerToken,
  WebinarEventHistoryDto,
  WebinarPresenceStatusDto,
} from '@/features/live/types';
import type {
  EvaluationTemplate,
  PublicEvergreenChatConfiguration,
  OfferTemplate,
  ParticipationStatusDto,
  PublicWebinarMe,
  QuitPage,
  QuizResults,
  QuizSubmission,
  QuizTemplate,
  SurveyResults,
  SurveyTemplate,
  ThankYouPage,
  WebinarResource,
  WebinarSlide,
} from '@/features/webinar/types';
import type { WorkspaceSocialMediaLink } from '@/features/workspace/types';
import {
  banPresenceParticipant,
  closeSession,
  deleteChatMessage,
  deleteWorkspaceChatMessage,
  fetchAdminChatToken,
  fetchAdminResourceState,
  fetchAttendeeInfo,
  fetchChatHistory,
  fetchEventHistory,
  fetchLivekitParticipants,
  fetchMyPublicQuizSubmission,
  fetchPlayback,
  fetchModeratorToken,
  fetchPresenterToken,
  fetchPresenceStatus,
  fetchPublicChatToken,
  fetchPublicEvaluation,
  fetchPublicOffer,
  fetchPublicParticipationStatuses,
  fetchPublicPlayback,
  fetchPublicQuitPage,
  fetchPublicQuiz,
  fetchPublicResource,
  fetchPublicResourceState,
  fetchPublicEvergreen,
  refreshEvergreenChatMode,
  fetchPublicSession,
  fetchPublicSlide,
  fetchPublicSlides,
  fetchPublicSurvey,
  fetchPublicThankYouPage,
  fetchPublicWebinarResources,
  fetchPublicWorkspaceSocialMediaLinks,
  fetchOfferPurchaseNotificationConfig,
  fetchRoleToken,
  fetchSessionPresence,
  fetchSpeakerTokens,
  fetchQuizResults,
  fetchOfferResults,
  fetchSurveyResults,
  fetchViewerToken,
  fetchWorkspaceChatMessages,
  muteTrack,
  pinChatMessage,
  pinWorkspaceChatMessage,
  postAnalyticsEvent,
  postAttendeeChatMessage,
  postChatReaction,
  postLiveReaction,
  postWorkspaceChatMessage,
  removeParticipant,
  rotateInviteToken,
  startCustomRecording,
  updateSessionLayout,
  startHlsEgress,
  startMp4Egress,
  startSession,
  stopEgress,
  submitPublicEvaluation,
  submitPublicQuitPage,
  submitPublicQuiz,
  submitPublicQuizPut,
  submitPublicSurvey,
  submitPublicSurveyPut,
  triggerAdminEvent,
  triggerEvent,
  unpinChatMessage,
  unpinWorkspaceChatMessage,
  updateOfferPurchaseNotificationConfig,
} from '@/features/live/liveThunks';
import { switchWorkspace } from '@/features/workspace/workspaceSlice';
import { fetchPublicWebinarSession } from '@/features/webinar/webinarThunks';
import { isPublicSessionApplicable } from '@/lib/publicSessionFreshness';
import { isForActivePublicSession, publicSessionReadKey } from '@/lib/publicSessionRead';
import { shouldHideDisplayedInteraction } from '@/lib/evergreenInteractionSchedule';

/** Participants utiles pour l’admin scène uniquement (pas les viewers LiveKit). */
function isStageLivekitIdentity(identity: string | null | undefined): boolean {
  if (!identity) return false;
  return identity.startsWith('presenter-') || identity.startsWith('speaker-');
}

const initialState: LiveState = {
  viewerToken: null,
  viewerTokenLoading: false,
  viewerTokenError: null,
  roleToken: null,
  roleTokenLoading: false,
  roleTokenError: null,
  attendeeInfo: null,
  attendeeInfoLoading: false,
  attendeeInfoError: null,
  chatReactionLoading: false,
  chatReactionError: null,
  chatMessageLoading: false,
  chatMessageError: null,
  chatHistory: [],
  chatHistoryLoading: false,
  chatHistoryError: null,
  pinMessageLoading: false,
  pinMessageError: null,
  unpinMessageLoading: false,
  unpinMessageError: null,
  deleteMessageLoading: false,
  deleteMessageError: null,
  hlsEgressId: null,
  hlsEgressLoading: false,
  hlsEgressError: null,
  mp4EgressId: null,
  mp4EgressLoading: false,
  mp4EgressError: null,
  stopEgressLoading: false,
  stopEgressError: null,
  startCustomRecordingLoading: false,
  startCustomRecordingError: null,
  streamStartStatus: 'IDLE',
  updateSessionLayoutLoading: false,
  updateSessionLayoutError: null,
  workspaceChatMessageLoading: false,
  workspaceChatMessageError: null,
  workspacePinMessageLoading: false,
  workspacePinMessageError: null,
  workspaceUnpinMessageLoading: false,
  workspaceUnpinMessageError: null,
  workspaceDeleteMessageLoading: false,
  workspaceDeleteMessageError: null,
  workspaceChatMessages: [],
  workspaceChatMessagesLoading: false,
  workspaceChatMessagesError: null,
  triggerEventLoading: false,
  triggerEventError: null,
  triggerAdminEventLoading: false,
  triggerAdminEventError: null,
  adminChatToken: null,
  adminChatTokenTarget: null,
  adminChatTokenRequestId: null,
  adminChatRole: null,
  adminChatTokenLoading: false,
  adminChatTokenError: null,
  publicChatToken: null,
  publicChatTokenLoading: false,
  publicChatTokenError: null,
  startSessionLoading: false,
  startSessionError: null,
  presenterToken: null,
  presenterTokenLoading: false,
  presenterTokenError: null,
  speakerTokens: [],
  speakerTokensLoading: false,
  speakerTokensError: null,
  moderatorToken: null,
  moderatorTokenLoading: false,
  moderatorTokenError: null,
  closeSessionLoading: false,
  closeSessionError: null,
  rotateInviteToken: null,
  rotateInviteTokenLoading: false,
  rotateInviteTokenError: null,
  livekitParticipants: [],
  livekitParticipantsLoading: false,
  livekitParticipantsError: null,
  sessionPresence: null,
  sessionPresenceLoading: false,
  sessionPresenceError: null,
  banPresenceParticipantLoading: false,
  banPresenceParticipantError: null,
  participantTrackPublications: {},
  pendingStageInvites: EMPTY_PENDING_INVITES,
  removeParticipantLoading: false,
  removeParticipantError: null,
  muteTrackLoading: false,
  muteTrackError: null,
  playbackInfo: null,
  playbackLoading: false,
  playbackError: null,
  adminResourceStates: [],
  adminResourceStatesLoading: false,
  adminResourceStatesError: null,
  liveReactionLoading: false,
  liveReactionError: null,
  publicResourceStates: [],
  publicResourceStatesLoading: false,
  publicResourceStatesError: null,
  publicSession: null,
  publicSessionLoading: false,
  publicSessionError: null,
  publicSessionKey: null,
  youtubeClosePending: false,
  publicEvergreen: null,
  publicEvergreenLoading: false,
  publicEvergreenError: null,
  publicOffer: null,
  publicOfferLoading: false,
  publicOfferError: null,
  openedOfferEmbedId: null,
  postAnalyticsEventLoading: false,
  postAnalyticsEventError: null,
  publicSurvey: null,
  publicSurveyLoading: false,
  publicSurveyError: null,
  publicQuiz: null,
  publicQuizLoading: false,
  publicQuizError: null,
  publicQuitPage: null,
  publicEvaluation: null,
  publicEvaluationLoading: false,
  publicEvaluationError: null,
  publicThankYouPage: null,
  publicThankYouPageLoading: false,
  publicThankYouPageError: null,
  publicParticipationStatuses: [],
  publicParticipationStatusesLoading: false,
  publicParticipationStatusesError: null,
  publicQuitPageLoading: false,
  publicQuitPageError: null,
  submitQuitPageLoading: false,
  submitQuitPageError: null,
  submitSurveyLoading: false,
  submitSurveyError: null,
  submitSurveyPutLoading: false,
  submitSurveyPutError: null,
  surveyResultsByTemplateId: {},
  surveyResultsLoading: false,
  surveyResultsError: null,
  quizResultsByTemplateId: {},
  quizResultsLoading: false,
  quizResultsError: null,
  offerResultsByOfferId: {},
  offerResultsLoading: false,
  offerResultsError: null,
  submitQuizLoading: false,
  submitQuizError: null,
  publicSlides: [],
  publicSlidesLoading: false,
  publicSlidesError: null,
  publicSlide: null,
  publicSlideLoading: false,
  publicSlideError: null,
  publicResources: [],
  publicResourcesLoading: false,
  publicResourcesError: null,
  publicResource: null,
  publicResourceLoading: false,
  publicResourceError: null,
  publicWorkspaceSocialMediaLinks: [],
  publicWorkspaceSocialMediaLinksLoading: false,
  publicWorkspaceSocialMediaLinksError: null,
  publicQuizSubmission: null,
  publicQuizSubmissionLoading: false,
  publicQuizSubmissionError: null,
  offerPurchaseNotificationConfigs: {},
  offerPurchaseNotificationConfigLoading: {},
  offerPurchaseNotificationConfigError: {},
  updateOfferPurchaseNotificationConfigLoading: {},
  updateOfferPurchaseNotificationConfigError: {},
  submitPublicEvaluationLoading: false,
  submitPublicEvaluationError: null,
  eventHistory: null,
  eventHistoryLoading: false,
  eventHistoryError: null,
  presenceStatus: null,
  presenceStatusLoading: false,
  presenceStatusError: null,
  evergreenChatModeSync: initialEvergreenChatModeSync,
};

const slice = createSlice({
  name: 'live',
  initialState,
  reducers: {
    setParticipantTrackPublications(state, action: PayloadAction<Record<string, boolean>>) {
      state.participantTrackPublications = action.payload;
    },
    stageInviteEventReceived(state, action: PayloadAction<InviteRequestEvent>) {
      state.pendingStageInvites = applyInviteRequestEvent(
        state.pendingStageInvites,
        action.payload,
      );
    },
    stageInvitesReconciled(
      state,
      action: PayloadAction<{ [K in keyof RoomSnapshot]: readonly string[] }>,
    ) {
      const { identitiesInRoom, identitiesInRoomFromApi, identitiesWithTracks } = action.payload;
      state.pendingStageInvites = reconcilePendingInvites(state.pendingStageInvites, {
        identitiesInRoom: new Set(identitiesInRoom),
        identitiesInRoomFromApi: new Set(identitiesInRoomFromApi),
        identitiesWithTracks: new Set(identitiesWithTracks),
      });
    },
    setStreamStartStatus(state, action: PayloadAction<StreamStartStatus>) {
      state.streamStartStatus = action.payload;
    },
    applyEvergreenChatModeChange(state, action: PayloadAction<ReplayedChatMode>) {
      state.evergreenChatModeSync = chatModeEventReceived(
        state.evergreenChatModeSync,
        action.payload,
      );
    },
    clearPublicOffer(state) {
      state.publicOffer = null;
      state.openedOfferEmbedId = null;
    },
    openOfferEmbed(state, action: PayloadAction<number>) {
      state.openedOfferEmbedId = action.payload;
    },
    closeOfferEmbed(state) {
      state.openedOfferEmbedId = null;
    },
    clearPublicSurvey(state) {
      state.publicSurvey = null;
    },
    clearPublicQuiz(state) {
      state.publicQuiz = null;
    },
    hidePublicOffer(state, action: PayloadAction<number>) {
      if (shouldHideDisplayedInteraction(state.publicOffer?.id, action.payload)) {
        state.publicOffer = null;
        state.openedOfferEmbedId = null;
      }
    },
    hidePublicSurvey(state, action: PayloadAction<number>) {
      if (shouldHideDisplayedInteraction(state.publicSurvey?.id, action.payload)) {
        state.publicSurvey = null;
      }
    },
    hidePublicQuiz(state, action: PayloadAction<number>) {
      if (shouldHideDisplayedInteraction(state.publicQuiz?.id, action.payload)) {
        state.publicQuiz = null;
      }
    },
    clearPublicResource(state) {
      state.publicResource = null;
    },
    clearPublicSlide(state) {
      state.publicSlide = null;
    },
    hydrateAttendeeInfoFromSubscriptionJwt(state, action: PayloadAction<PublicWebinarMe>) {
      state.attendeeInfo = action.payload;
      state.attendeeInfoLoading = false;
      state.attendeeInfoError = null;
    },
    setYoutubeClosePending(state, action: PayloadAction<boolean>) {
      state.youtubeClosePending = action.payload;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchViewerToken.pending, (state) => {
      state.viewerTokenLoading = true;
      state.viewerTokenError = null;
    });
    builder.addCase(fetchViewerToken.fulfilled, (state, action: PayloadAction<ViewerToken>) => {
      state.viewerTokenLoading = false;
      state.viewerToken = action.payload;
      state.viewerTokenError = null;
    });
    builder.addCase(fetchViewerToken.rejected, (state, action) => {
      state.viewerTokenLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.viewerTokenError =
        payload?.message ?? action.error.message ?? 'Failed to fetch viewer token';
    });

    builder.addCase(fetchRoleToken.pending, (state) => {
      state.roleTokenLoading = true;
      state.roleTokenError = null;
    });
    builder.addCase(fetchRoleToken.fulfilled, (state, action: PayloadAction<RoleToken>) => {
      state.roleTokenLoading = false;
      state.roleToken = action.payload;
      state.roleTokenError = null;
    });
    builder.addCase(fetchRoleToken.rejected, (state, action) => {
      state.roleTokenLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.roleTokenError =
        payload?.message ?? action.error.message ?? 'Failed to fetch role token';
    });

    builder.addCase(rotateInviteToken.pending, (state) => {
      state.rotateInviteTokenLoading = true;
      state.rotateInviteTokenError = null;
    });
    builder.addCase(
      rotateInviteToken.fulfilled,
      (state, action: PayloadAction<RotatedInviteToken>) => {
        state.rotateInviteTokenLoading = false;
        state.rotateInviteToken = action.payload;
        state.rotateInviteTokenError = null;
      },
    );
    builder.addCase(rotateInviteToken.rejected, (state, action) => {
      state.rotateInviteTokenLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.rotateInviteTokenError =
        payload?.message ?? action.error.message ?? 'Failed to rotate invite token';
    });

    builder.addCase(fetchLivekitParticipants.pending, (state) => {
      state.livekitParticipantsLoading = true;
      state.livekitParticipantsError = null;
    });
    builder.addCase(
      fetchLivekitParticipants.fulfilled,
      (state, action: PayloadAction<LivekitParticipant[]>) => {
        state.livekitParticipantsLoading = false;
        state.livekitParticipants = (action.payload ?? []).filter((p) =>
          isStageLivekitIdentity(p.identity),
        );
        state.livekitParticipantsError = null;
      },
    );
    builder.addCase(fetchLivekitParticipants.rejected, (state, action) => {
      state.livekitParticipantsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.livekitParticipantsError =
        payload?.message ?? action.error.message ?? 'Failed to fetch livekit participants';
    });

    builder.addCase(fetchSessionPresence.pending, (state) => {
      state.sessionPresenceLoading = true;
      state.sessionPresenceError = null;
    });
    builder.addCase(
      fetchSessionPresence.fulfilled,
      (state, action: PayloadAction<SessionPresenceDto>) => {
        state.sessionPresenceLoading = false;
        state.sessionPresence = action.payload;
        state.sessionPresenceError = null;
      },
    );
    builder.addCase(fetchSessionPresence.rejected, (state, action) => {
      state.sessionPresenceLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sessionPresenceError =
        payload?.message ?? action.error.message ?? 'Failed to fetch session presence';
    });

    builder.addCase(banPresenceParticipant.pending, (state) => {
      state.banPresenceParticipantLoading = true;
      state.banPresenceParticipantError = null;
    });
    builder.addCase(banPresenceParticipant.fulfilled, (state) => {
      state.banPresenceParticipantLoading = false;
      state.banPresenceParticipantError = null;
    });
    builder.addCase(banPresenceParticipant.rejected, (state, action) => {
      state.banPresenceParticipantLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.banPresenceParticipantError =
        payload?.message ?? action.error.message ?? 'Failed to ban presence participant';
    });

    builder.addCase(fetchAttendeeInfo.pending, (state) => {
      state.attendeeInfoLoading = true;
      state.attendeeInfoError = null;
    });
    builder.addCase(
      fetchAttendeeInfo.fulfilled,
      (state, action: PayloadAction<PublicWebinarMe>) => {
        state.attendeeInfoLoading = false;
        state.attendeeInfo = action.payload;
        state.attendeeInfoError = null;
      },
    );
    builder.addCase(fetchAttendeeInfo.rejected, (state, action) => {
      state.attendeeInfoLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.attendeeInfoError =
        payload?.message ?? action.error.message ?? 'Failed to fetch attendee info';
    });

    builder.addCase(postChatReaction.pending, (state) => {
      state.chatReactionLoading = true;
      state.chatReactionError = null;
    });
    builder.addCase(postChatReaction.fulfilled, (state) => {
      state.chatReactionLoading = false;
      state.chatReactionError = null;
    });
    builder.addCase(postChatReaction.rejected, (state, action) => {
      state.chatReactionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.chatReactionError =
        payload?.message ?? action.error.message ?? 'Failed to post chat reaction';
    });

    builder.addCase(postLiveReaction.pending, (state) => {
      state.liveReactionLoading = true;
      state.liveReactionError = null;
    });
    builder.addCase(postLiveReaction.fulfilled, (state) => {
      state.liveReactionLoading = false;
      state.liveReactionError = null;
    });
    builder.addCase(postLiveReaction.rejected, (state, action) => {
      state.liveReactionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.liveReactionError =
        payload?.message ?? action.error.message ?? 'Failed to post live reaction';
    });

    builder.addCase(postAnalyticsEvent.pending, (state) => {
      state.postAnalyticsEventLoading = true;
      state.postAnalyticsEventError = null;
    });
    builder.addCase(postAnalyticsEvent.fulfilled, (state) => {
      state.postAnalyticsEventLoading = false;
      state.postAnalyticsEventError = null;
    });
    builder.addCase(postAnalyticsEvent.rejected, (state, action) => {
      state.postAnalyticsEventLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.postAnalyticsEventError =
        payload?.message ?? action.error.message ?? 'Failed to post analytics event';
    });

    builder.addCase(postAttendeeChatMessage.pending, (state) => {
      state.chatMessageLoading = true;
      state.chatMessageError = null;
    });
    builder.addCase(postAttendeeChatMessage.fulfilled, (state, action) => {
      state.chatMessageLoading = false;
      state.chatMessageError = null;
      if (action.payload) {
        state.chatHistory = state.chatHistory.concat(action.payload);
      }
    });
    builder.addCase(postAttendeeChatMessage.rejected, (state, action) => {
      state.chatMessageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.chatMessageError =
        payload?.message ?? action.error.message ?? 'Failed to post chat message';
    });

    builder.addCase(fetchChatHistory.pending, (state) => {
      state.chatHistoryLoading = true;
      state.chatHistoryError = null;
    });
    builder.addCase(
      fetchChatHistory.fulfilled,
      (state, action: PayloadAction<ChatMessageDto[]>) => {
        state.chatHistoryLoading = false;
        state.chatHistory = action.payload;
        state.chatHistoryError = null;
      },
    );
    builder.addCase(fetchChatHistory.rejected, (state, action) => {
      state.chatHistoryLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.chatHistoryError =
        payload?.message ?? action.error.message ?? 'Failed to fetch chat history';
    });

    builder.addCase(pinChatMessage.pending, (state) => {
      state.pinMessageLoading = true;
      state.pinMessageError = null;
    });
    builder.addCase(pinChatMessage.fulfilled, (state) => {
      state.pinMessageLoading = false;
      state.pinMessageError = null;
    });
    builder.addCase(pinChatMessage.rejected, (state, action) => {
      state.pinMessageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.pinMessageError =
        payload?.message ?? action.error.message ?? 'Failed to pin chat message';
    });

    builder.addCase(unpinChatMessage.pending, (state) => {
      state.unpinMessageLoading = true;
      state.unpinMessageError = null;
    });
    builder.addCase(unpinChatMessage.fulfilled, (state) => {
      state.unpinMessageLoading = false;
      state.unpinMessageError = null;
    });
    builder.addCase(unpinChatMessage.rejected, (state, action) => {
      state.unpinMessageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.unpinMessageError =
        payload?.message ?? action.error.message ?? 'Failed to unpin chat message';
    });

    builder.addCase(deleteChatMessage.pending, (state) => {
      state.deleteMessageLoading = true;
      state.deleteMessageError = null;
    });
    builder.addCase(deleteChatMessage.fulfilled, (state) => {
      state.deleteMessageLoading = false;
      state.deleteMessageError = null;
    });
    builder.addCase(deleteChatMessage.rejected, (state, action) => {
      state.deleteMessageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.deleteMessageError =
        payload?.message ?? action.error.message ?? 'Failed to delete chat message';
    });

    builder.addCase(startHlsEgress.pending, (state) => {
      state.hlsEgressLoading = true;
      state.hlsEgressError = null;
    });
    builder.addCase(startHlsEgress.fulfilled, (state, action: PayloadAction<string>) => {
      state.hlsEgressLoading = false;
      state.hlsEgressId = action.payload;
      state.hlsEgressError = null;
    });
    builder.addCase(startHlsEgress.rejected, (state, action) => {
      state.hlsEgressLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.hlsEgressError =
        payload?.message ?? action.error.message ?? 'Failed to start HLS egress';
    });

    builder.addCase(startMp4Egress.pending, (state) => {
      state.mp4EgressLoading = true;
      state.mp4EgressError = null;
    });
    builder.addCase(startMp4Egress.fulfilled, (state, action: PayloadAction<string>) => {
      state.mp4EgressLoading = false;
      state.mp4EgressId = action.payload;
      state.mp4EgressError = null;
    });
    builder.addCase(startMp4Egress.rejected, (state, action) => {
      state.mp4EgressLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.mp4EgressError =
        payload?.message ?? action.error.message ?? 'Failed to start MP4 egress';
    });

    builder.addCase(stopEgress.pending, (state) => {
      state.stopEgressLoading = true;
      state.stopEgressError = null;
    });
    builder.addCase(stopEgress.fulfilled, (state) => {
      state.stopEgressLoading = false;
      state.stopEgressError = null;
    });
    builder.addCase(stopEgress.rejected, (state, action) => {
      state.stopEgressLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.stopEgressError = payload?.message ?? action.error.message ?? 'Failed to stop egress';
    });

    builder.addCase(startCustomRecording.pending, (state) => {
      state.startCustomRecordingLoading = true;
      state.startCustomRecordingError = null;
    });
    builder.addCase(startCustomRecording.fulfilled, (state, action) => {
      state.startCustomRecordingLoading = false;
      state.startCustomRecordingError = null;
      state.streamStartStatus = action.payload.streamStartStatus;
    });
    builder.addCase(startCustomRecording.rejected, (state, action) => {
      state.startCustomRecordingLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.startCustomRecordingError =
        payload?.message ?? action.error.message ?? 'Failed to start custom recording';
    });

    builder.addCase(updateSessionLayout.pending, (state) => {
      state.updateSessionLayoutLoading = true;
      state.updateSessionLayoutError = null;
    });
    builder.addCase(updateSessionLayout.fulfilled, (state) => {
      state.updateSessionLayoutLoading = false;
      state.updateSessionLayoutError = null;
    });
    builder.addCase(updateSessionLayout.rejected, (state, action) => {
      state.updateSessionLayoutLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.updateSessionLayoutError =
        payload?.message ?? action.error.message ?? 'Failed to update session layout';
    });

    builder.addCase(fetchWorkspaceChatMessages.pending, (state) => {
      state.workspaceChatMessagesLoading = true;
      state.workspaceChatMessagesError = null;
    });
    builder.addCase(
      fetchWorkspaceChatMessages.fulfilled,
      (state, action: PayloadAction<ChatMessageDto[]>) => {
        state.workspaceChatMessagesLoading = false;
        state.workspaceChatMessages = action.payload;
        state.workspaceChatMessagesError = null;
      },
    );
    builder.addCase(fetchWorkspaceChatMessages.rejected, (state, action) => {
      state.workspaceChatMessagesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.workspaceChatMessagesError =
        payload?.message ?? action.error.message ?? 'Failed to fetch workspace chat messages';
    });

    builder.addCase(postWorkspaceChatMessage.pending, (state) => {
      state.workspaceChatMessageLoading = true;
      state.workspaceChatMessageError = null;
    });
    builder.addCase(postWorkspaceChatMessage.fulfilled, (state, action) => {
      state.workspaceChatMessageLoading = false;
      state.workspaceChatMessageError = null;
      if (action.payload) {
        state.workspaceChatMessages = state.workspaceChatMessages.concat(action.payload);
      }
    });
    builder.addCase(postWorkspaceChatMessage.rejected, (state, action) => {
      state.workspaceChatMessageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.workspaceChatMessageError =
        payload?.message ?? action.error.message ?? 'Failed to post workspace chat message';
    });

    builder.addCase(pinWorkspaceChatMessage.pending, (state) => {
      state.workspacePinMessageLoading = true;
      state.workspacePinMessageError = null;
    });
    builder.addCase(pinWorkspaceChatMessage.fulfilled, (state) => {
      state.workspacePinMessageLoading = false;
      state.workspacePinMessageError = null;
    });
    builder.addCase(pinWorkspaceChatMessage.rejected, (state, action) => {
      state.workspacePinMessageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.workspacePinMessageError =
        payload?.message ?? action.error.message ?? 'Failed to pin workspace chat message';
    });

    builder.addCase(unpinWorkspaceChatMessage.pending, (state) => {
      state.workspaceUnpinMessageLoading = true;
      state.workspaceUnpinMessageError = null;
    });
    builder.addCase(unpinWorkspaceChatMessage.fulfilled, (state) => {
      state.workspaceUnpinMessageLoading = false;
      state.workspaceUnpinMessageError = null;
    });
    builder.addCase(unpinWorkspaceChatMessage.rejected, (state, action) => {
      state.workspaceUnpinMessageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.workspaceUnpinMessageError =
        payload?.message ?? action.error.message ?? 'Failed to unpin workspace chat message';
    });

    builder.addCase(deleteWorkspaceChatMessage.pending, (state) => {
      state.workspaceDeleteMessageLoading = true;
      state.workspaceDeleteMessageError = null;
    });
    builder.addCase(deleteWorkspaceChatMessage.fulfilled, (state) => {
      state.workspaceDeleteMessageLoading = false;
      state.workspaceDeleteMessageError = null;
    });
    builder.addCase(deleteWorkspaceChatMessage.rejected, (state, action) => {
      state.workspaceDeleteMessageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.workspaceDeleteMessageError =
        payload?.message ?? action.error.message ?? 'Failed to delete workspace chat message';
    });

    builder.addCase(triggerEvent.pending, (state) => {
      state.triggerEventLoading = true;
      state.triggerEventError = null;
    });
    builder.addCase(triggerEvent.fulfilled, (state) => {
      state.triggerEventLoading = false;
      state.triggerEventError = null;
    });
    builder.addCase(triggerEvent.rejected, (state, action) => {
      state.triggerEventLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.triggerEventError =
        payload?.message ?? action.error.message ?? 'Failed to trigger event';
    });

    builder.addCase(triggerAdminEvent.pending, (state) => {
      state.triggerAdminEventLoading = true;
      state.triggerAdminEventError = null;
    });
    builder.addCase(triggerAdminEvent.fulfilled, (state) => {
      state.triggerAdminEventLoading = false;
      state.triggerAdminEventError = null;
    });
    builder.addCase(triggerAdminEvent.rejected, (state, action) => {
      state.triggerAdminEventLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.triggerAdminEventError =
        payload?.message ?? action.error.message ?? 'Failed to trigger admin event';
    });

    builder.addCase(fetchAdminChatToken.pending, (state, action) => {
      state.adminChatTokenRequestId = action.meta.requestId;
      state.adminChatTokenLoading = true;
      state.adminChatTokenError = null;
    });
    builder.addCase(fetchAdminChatToken.fulfilled, (state, action) => {
      // Une navigation A → B laisse la demande de A en vol : sa réponse ne doit plus rien écrire.
      if (state.adminChatTokenRequestId !== action.meta.requestId) {
        return;
      }
      state.adminChatTokenLoading = false;
      state.adminChatToken = action.payload;
      state.adminChatTokenTarget = action.meta.arg;
      state.adminChatRole = extractRoleFromClientId(action.payload.clientId);
      state.adminChatTokenError = null;
    });
    builder.addCase(fetchAdminChatToken.rejected, (state, action) => {
      if (state.adminChatTokenRequestId !== action.meta.requestId) {
        return;
      }
      state.adminChatTokenLoading = false;
      state.adminChatToken = null;
      state.adminChatTokenTarget = null;
      state.adminChatRole = null;
      const payload = action.payload as { message?: string } | undefined;
      state.adminChatTokenError =
        payload?.message ?? action.error.message ?? 'Failed to fetch chat token';
    });

    builder.addCase(fetchPublicChatToken.pending, (state) => {
      state.publicChatTokenLoading = true;
      state.publicChatTokenError = null;
    });
    builder.addCase(fetchPublicChatToken.fulfilled, (state, action: PayloadAction<ChatToken>) => {
      state.publicChatTokenLoading = false;
      state.publicChatToken = action.payload;
      state.publicChatTokenError = null;
    });
    builder.addCase(fetchPublicChatToken.rejected, (state, action) => {
      state.publicChatTokenLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicChatTokenError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public chat token';
    });

    builder.addCase(startSession.pending, (state) => {
      state.startSessionLoading = true;
      state.startSessionError = null;
    });
    builder.addCase(startSession.fulfilled, (state) => {
      state.startSessionLoading = false;
      state.startSessionError = null;
    });
    builder.addCase(startSession.rejected, (state, action) => {
      state.startSessionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.startSessionError =
        payload?.message ?? action.error.message ?? 'Failed to start session';
    });

    builder.addCase(fetchPresenterToken.pending, (state) => {
      state.presenterTokenLoading = true;
      state.presenterTokenError = null;
    });
    builder.addCase(
      fetchPresenterToken.fulfilled,
      (state, action: PayloadAction<PresenterToken>) => {
        state.presenterTokenLoading = false;
        state.presenterToken = action.payload;
        state.presenterTokenError = null;
      },
    );
    builder.addCase(fetchPresenterToken.rejected, (state, action) => {
      state.presenterTokenLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.presenterTokenError =
        payload?.message ?? action.error.message ?? 'Failed to fetch presenter token';
    });

    builder.addCase(fetchModeratorToken.pending, (state) => {
      state.moderatorTokenLoading = true;
      state.moderatorTokenError = null;
    });
    builder.addCase(
      fetchModeratorToken.fulfilled,
      (state, action: PayloadAction<ModeratorToken>) => {
        state.moderatorTokenLoading = false;
        state.moderatorToken = action.payload;
        state.moderatorTokenError = null;
      },
    );
    builder.addCase(fetchModeratorToken.rejected, (state, action) => {
      state.moderatorTokenLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.moderatorTokenError =
        payload?.message ?? action.error.message ?? 'Failed to fetch moderator token';
    });

    builder.addCase(fetchSpeakerTokens.pending, (state) => {
      state.speakerTokensLoading = true;
      state.speakerTokensError = null;
    });
    builder.addCase(
      fetchSpeakerTokens.fulfilled,
      (state, action: PayloadAction<SpeakerToken[]>) => {
        state.speakerTokensLoading = false;
        state.speakerTokens = action.payload;
        state.speakerTokensError = null;
      },
    );
    builder.addCase(fetchSpeakerTokens.rejected, (state, action) => {
      state.speakerTokensLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.speakerTokensError =
        payload?.message ?? action.error.message ?? 'Failed to fetch speaker tokens';
    });

    builder.addCase(closeSession.pending, (state) => {
      state.closeSessionLoading = true;
      state.closeSessionError = null;
    });
    builder.addCase(closeSession.fulfilled, (state) => {
      state.closeSessionLoading = false;
      state.closeSessionError = null;
    });
    builder.addCase(closeSession.rejected, (state, action) => {
      state.closeSessionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.closeSessionError =
        payload?.message ?? action.error.message ?? 'Failed to close session';
    });

    builder.addCase(removeParticipant.pending, (state) => {
      state.removeParticipantLoading = true;
      state.removeParticipantError = null;
    });
    builder.addCase(removeParticipant.fulfilled, (state) => {
      state.removeParticipantLoading = false;
      state.removeParticipantError = null;
    });
    builder.addCase(removeParticipant.rejected, (state, action) => {
      state.removeParticipantLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.removeParticipantError =
        payload?.message ?? action.error.message ?? 'Failed to remove participant';
    });

    builder.addCase(muteTrack.pending, (state) => {
      state.muteTrackLoading = true;
      state.muteTrackError = null;
    });
    builder.addCase(muteTrack.fulfilled, (state) => {
      state.muteTrackLoading = false;
      state.muteTrackError = null;
    });
    builder.addCase(muteTrack.rejected, (state, action) => {
      state.muteTrackLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.muteTrackError = payload?.message ?? action.error.message ?? 'Failed to mute track';
    });

    builder.addCase(fetchPlayback.pending, (state) => {
      state.playbackLoading = true;
      state.playbackError = null;
    });
    builder.addCase(fetchPlayback.fulfilled, (state, action: PayloadAction<PlaybackInfo>) => {
      state.playbackLoading = false;
      state.playbackInfo = action.payload;
      state.playbackError = null;
    });
    builder.addCase(fetchPlayback.rejected, (state, action) => {
      state.playbackLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.playbackError =
        payload?.message ?? action.error.message ?? 'Failed to fetch playback info';
    });

    builder.addCase(fetchPublicPlayback.pending, (state) => {
      state.playbackLoading = true;
      state.playbackError = null;
    });
    builder.addCase(fetchPublicPlayback.fulfilled, (state, action: PayloadAction<PlaybackInfo>) => {
      state.playbackLoading = false;
      state.playbackInfo = action.payload;
      state.playbackError = null;
    });
    builder.addCase(fetchPublicPlayback.rejected, (state, action) => {
      state.playbackLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.playbackError =
        payload?.message ?? action.error.message ?? 'Failed to fetch playback info';
    });

    builder.addCase(fetchPublicSession.pending, (state, action) => {
      state.publicSessionLoading = true;
      state.publicSessionError = null;
      state.publicSessionKey = publicSessionReadKey(action.meta.arg);
    });
    builder.addCase(fetchPublicWebinarSession.pending, (state, action) => {
      state.publicSessionKey = publicSessionReadKey(action.meta.arg);
    });
    // Un résultat tardif d'une autre session (route quittée) ne remplace pas la session courante.
    builder.addCase(fetchPublicSession.fulfilled, (state, action) => {
      if (!isForActivePublicSession(state.publicSessionKey, action.meta.arg)) return;
      state.publicSessionLoading = false;
      if (isPublicSessionApplicable(state.publicSession, action.payload)) {
        state.publicSession = action.payload;
      }
      state.publicSessionError = null;
    });
    builder.addCase(fetchPublicSession.rejected, (state, action) => {
      if (!isForActivePublicSession(state.publicSessionKey, action.meta.arg)) return;
      state.publicSessionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicSessionError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public session';
    });

    // Même endpoint que fetchPublicSession, chargé depuis la salle d'attente : on avance la copie
    // du live avec, sinon elle reste sur le statut d'avant le démarrage et renvoie le spectateur
    // en arrière au moment de la bascule.
    builder.addCase(fetchPublicWebinarSession.fulfilled, (state, action) => {
      // Miroir seul : on n'a pas déclenché ce thunk, on ne touche donc ni l'erreur ni le
      // drapeau de chargement de ce slice, qui appartiennent à son propre cycle.
      if (!isForActivePublicSession(state.publicSessionKey, action.meta.arg)) return;
      if (!isPublicSessionApplicable(state.publicSession, action.payload)) return;
      state.publicSession = action.payload;
    });

    builder.addCase(fetchPublicEvergreen.pending, (state) => {
      state.publicEvergreenLoading = true;
      state.publicEvergreenError = null;
    });
    builder.addCase(
      fetchPublicEvergreen.fulfilled,
      (state, action: PayloadAction<PublicEvergreenChatConfiguration>) => {
        state.publicEvergreenLoading = false;
        state.publicEvergreen = action.payload;
        state.publicEvergreenError = null;
      },
    );
    builder.addCase(fetchPublicEvergreen.rejected, (state, action) => {
      state.publicEvergreenLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicEvergreenError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public evergreen';
    });

    builder.addCase(fetchPublicOffer.pending, (state) => {
      state.publicOfferLoading = true;
      state.publicOfferError = null;
    });
    builder.addCase(fetchPublicOffer.fulfilled, (state, action: PayloadAction<OfferTemplate>) => {
      state.publicOfferLoading = false;
      if (state.openedOfferEmbedId !== action.payload.id) state.openedOfferEmbedId = null;
      state.publicOffer = action.payload;
      state.publicOfferError = null;
    });
    builder.addCase(fetchPublicOffer.rejected, (state, action) => {
      state.publicOfferLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicOfferError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public offer';
    });

    builder.addCase(fetchAdminResourceState.pending, (state) => {
      state.adminResourceStatesLoading = true;
      state.adminResourceStatesError = null;
    });
    builder.addCase(
      fetchAdminResourceState.fulfilled,
      (state, action: PayloadAction<LiveSessionResourceStateDto[]>) => {
        state.adminResourceStatesLoading = false;
        state.adminResourceStates = action.payload;
        state.adminResourceStatesError = null;
      },
    );
    builder.addCase(fetchAdminResourceState.rejected, (state, action) => {
      state.adminResourceStatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.adminResourceStatesError =
        payload?.message ?? action.error.message ?? 'Failed to fetch admin resource state';
    });

    builder.addCase(fetchPublicResourceState.pending, (state) => {
      state.publicResourceStatesLoading = true;
      state.publicResourceStatesError = null;
    });
    builder.addCase(
      fetchPublicResourceState.fulfilled,
      (state, action: PayloadAction<LiveSessionResourceStateDto[]>) => {
        state.publicResourceStatesLoading = false;
        state.publicResourceStates = action.payload;
        state.publicResourceStatesError = null;
      },
    );
    builder.addCase(fetchPublicResourceState.rejected, (state, action) => {
      state.publicResourceStatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicResourceStatesError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public resource state';
    });

    builder.addCase(fetchPublicSurvey.pending, (state) => {
      state.publicSurveyLoading = true;
      state.publicSurveyError = null;
    });
    builder.addCase(fetchPublicSurvey.fulfilled, (state, action: PayloadAction<SurveyTemplate>) => {
      state.publicSurveyLoading = false;
      state.publicSurvey = action.payload;
      state.publicSurveyError = null;
    });
    builder.addCase(fetchPublicSurvey.rejected, (state, action) => {
      state.publicSurveyLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicSurveyError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public survey';
    });

    builder.addCase(fetchPublicQuiz.pending, (state) => {
      state.publicQuizLoading = true;
      state.publicQuizError = null;
    });
    builder.addCase(fetchPublicQuiz.fulfilled, (state, action: PayloadAction<QuizTemplate>) => {
      state.publicQuizLoading = false;
      state.publicQuiz = action.payload;
      state.publicQuizError = null;
    });
    builder.addCase(fetchPublicQuiz.rejected, (state, action) => {
      state.publicQuizLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicQuizError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public quiz';
    });

    builder.addCase(fetchPublicQuitPage.pending, (state) => {
      state.publicQuitPageLoading = true;
      state.publicQuitPageError = null;
    });
    builder.addCase(fetchPublicQuitPage.fulfilled, (state, action: PayloadAction<QuitPage>) => {
      state.publicQuitPageLoading = false;
      state.publicQuitPage = action.payload;
      state.publicQuitPageError = null;
    });
    builder.addCase(fetchPublicQuitPage.rejected, (state, action) => {
      state.publicQuitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicQuitPageError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public quit page';
    });

    builder.addCase(fetchPublicEvaluation.pending, (state) => {
      state.publicEvaluationLoading = true;
      state.publicEvaluationError = null;
    });
    builder.addCase(
      fetchPublicEvaluation.fulfilled,
      (state, action: PayloadAction<EvaluationTemplate>) => {
        state.publicEvaluationLoading = false;
        state.publicEvaluation = action.payload;
        state.publicEvaluationError = null;
      },
    );
    builder.addCase(fetchPublicEvaluation.rejected, (state, action) => {
      state.publicEvaluationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicEvaluationError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public evaluation';
    });

    builder.addCase(fetchPublicThankYouPage.pending, (state) => {
      state.publicThankYouPageLoading = true;
      state.publicThankYouPageError = null;
    });
    builder.addCase(
      fetchPublicThankYouPage.fulfilled,
      (state, action: PayloadAction<ThankYouPage>) => {
        state.publicThankYouPageLoading = false;
        state.publicThankYouPage = action.payload;
        state.publicThankYouPageError = null;
      },
    );
    builder.addCase(fetchPublicThankYouPage.rejected, (state, action) => {
      state.publicThankYouPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicThankYouPageError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public thank you page';
    });

    builder.addCase(fetchPublicParticipationStatuses.pending, (state) => {
      state.publicParticipationStatusesLoading = true;
      state.publicParticipationStatusesError = null;
    });
    builder.addCase(
      fetchPublicParticipationStatuses.fulfilled,
      (state, action: PayloadAction<ParticipationStatusDto[]>) => {
        state.publicParticipationStatusesLoading = false;
        state.publicParticipationStatuses = action.payload;
        state.publicParticipationStatusesError = null;
      },
    );
    builder.addCase(fetchPublicParticipationStatuses.rejected, (state, action) => {
      state.publicParticipationStatusesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicParticipationStatusesError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public participation statuses';
    });

    builder.addCase(submitPublicQuitPage.pending, (state) => {
      state.submitQuitPageLoading = true;
      state.submitQuitPageError = null;
    });
    builder.addCase(submitPublicQuitPage.fulfilled, (state) => {
      state.submitQuitPageLoading = false;
      state.submitQuitPageError = null;
    });
    builder.addCase(submitPublicQuitPage.rejected, (state, action) => {
      state.submitQuitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.submitQuitPageError =
        payload?.message ?? action.error.message ?? 'Failed to submit public quit page';
    });

    builder.addCase(submitPublicSurvey.pending, (state) => {
      state.submitSurveyLoading = true;
      state.submitSurveyError = null;
    });
    builder.addCase(submitPublicSurvey.fulfilled, (state) => {
      state.submitSurveyLoading = false;
      state.submitSurveyError = null;
    });
    builder.addCase(submitPublicSurvey.rejected, (state, action) => {
      state.submitSurveyLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.submitSurveyError =
        payload?.message ?? action.error.message ?? 'Failed to submit public survey';
    });

    builder.addCase(submitPublicSurveyPut.pending, (state) => {
      state.submitSurveyPutLoading = true;
      state.submitSurveyPutError = null;
    });
    builder.addCase(submitPublicSurveyPut.fulfilled, (state) => {
      state.submitSurveyPutLoading = false;
      state.submitSurveyPutError = null;
    });
    builder.addCase(submitPublicSurveyPut.rejected, (state, action) => {
      state.submitSurveyPutLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.submitSurveyPutError =
        payload?.message ?? action.error.message ?? 'Failed to submit public survey';
    });

    builder.addCase(fetchSurveyResults.pending, (state) => {
      state.surveyResultsLoading = true;
      state.surveyResultsError = null;
    });
    builder.addCase(fetchSurveyResults.fulfilled, (state, action: PayloadAction<SurveyResults>) => {
      state.surveyResultsLoading = false;
      state.surveyResultsByTemplateId[action.payload.templateId] = action.payload;
      state.surveyResultsError = null;
    });
    builder.addCase(fetchSurveyResults.rejected, (state, action) => {
      state.surveyResultsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyResultsError =
        payload?.message ?? action.error.message ?? 'Failed to fetch survey results';
    });

    builder.addCase(fetchQuizResults.pending, (state) => {
      state.quizResultsLoading = true;
      state.quizResultsError = null;
    });
    builder.addCase(fetchQuizResults.fulfilled, (state, action: PayloadAction<QuizResults>) => {
      state.quizResultsLoading = false;
      if (!state.quizResultsByTemplateId) state.quizResultsByTemplateId = {};
      state.quizResultsByTemplateId[action.payload.templateId] = action.payload;
      state.quizResultsError = null;
    });
    builder.addCase(fetchQuizResults.rejected, (state, action) => {
      state.quizResultsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizResultsError =
        payload?.message ?? action.error.message ?? 'Failed to fetch quiz results';
    });

    builder.addCase(fetchOfferResults.pending, (state) => {
      state.offerResultsLoading = true;
      state.offerResultsError = null;
    });
    builder.addCase(fetchOfferResults.fulfilled, (state, action) => {
      state.offerResultsLoading = false;
      const offerId = action.meta.arg.offerId;
      if (!state.offerResultsByOfferId) state.offerResultsByOfferId = {};
      state.offerResultsByOfferId[offerId] = {
        ...action.payload,
        offerId,
        webinarId: action.meta.arg.webinarId,
      };
      state.offerResultsError = null;
    });
    builder.addCase(fetchOfferResults.rejected, (state, action) => {
      state.offerResultsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.offerResultsError =
        payload?.message ?? action.error.message ?? 'Failed to fetch offer results';
    });

    builder.addCase(submitPublicQuiz.pending, (state) => {
      state.submitQuizLoading = true;
      state.submitQuizError = null;
    });
    builder.addCase(submitPublicQuiz.fulfilled, (state) => {
      state.submitQuizLoading = false;
      state.submitQuizError = null;
    });
    builder.addCase(submitPublicQuiz.rejected, (state, action) => {
      state.submitQuizLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.submitQuizError =
        payload?.message ?? action.error.message ?? 'Failed to submit public quiz';
    });

    builder.addCase(submitPublicQuizPut.pending, (state) => {
      state.submitQuizLoading = true;
      state.submitQuizError = null;
    });
    builder.addCase(submitPublicQuizPut.fulfilled, (state) => {
      state.submitQuizLoading = false;
      state.submitQuizError = null;
    });
    builder.addCase(submitPublicQuizPut.rejected, (state, action) => {
      state.submitQuizLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.submitQuizError =
        payload?.message ?? action.error.message ?? 'Failed to update public quiz';
    });

    builder.addCase(fetchMyPublicQuizSubmission.pending, (state) => {
      state.publicQuizSubmissionLoading = true;
      state.publicQuizSubmissionError = null;
    });
    builder.addCase(
      fetchMyPublicQuizSubmission.fulfilled,
      (state, action: PayloadAction<QuizSubmission | null>) => {
        state.publicQuizSubmissionLoading = false;
        state.publicQuizSubmission = action.payload;
        state.publicQuizSubmissionError = null;
      },
    );
    builder.addCase(fetchMyPublicQuizSubmission.rejected, (state, action) => {
      state.publicQuizSubmissionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicQuizSubmissionError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public quiz submission';
    });

    builder.addCase(fetchPublicSlides.pending, (state) => {
      state.publicSlidesLoading = true;
      state.publicSlidesError = null;
    });
    builder.addCase(
      fetchPublicSlides.fulfilled,
      (
        state,
        action: PayloadAction<{
          content: WebinarSlide[];
          totalElements: number;
          totalPages: number;
          size: number;
          number: number;
        }>,
      ) => {
        state.publicSlidesLoading = false;
        state.publicSlides = action.payload.content;
        state.publicSlidesError = null;
      },
    );
    builder.addCase(fetchPublicSlides.rejected, (state, action) => {
      state.publicSlidesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicSlidesError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public slides';
    });

    builder.addCase(fetchPublicSlide.pending, (state) => {
      state.publicSlideLoading = true;
      state.publicSlideError = null;
    });
    builder.addCase(fetchPublicSlide.fulfilled, (state, action: PayloadAction<WebinarSlide>) => {
      state.publicSlideLoading = false;
      state.publicSlide = action.payload;
      state.publicSlideError = null;
    });
    builder.addCase(fetchPublicSlide.rejected, (state, action) => {
      state.publicSlideLoading = false;
      /** Si le fetch échoue (ex: 404 slide supprimée), on ne garde pas une slide obsolète affichée. */
      state.publicSlide = null;
      const payload = action.payload as { message?: string } | undefined;
      state.publicSlideError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public slide';
    });

    builder.addCase(fetchPublicWebinarResources.pending, (state) => {
      state.publicResourcesLoading = true;
      state.publicResourcesError = null;
    });
    builder.addCase(
      fetchPublicWebinarResources.fulfilled,
      (
        state,
        action: PayloadAction<{
          content: WebinarResource[];
          totalElements: number;
          totalPages: number;
          size: number;
          number: number;
        }>,
      ) => {
        state.publicResourcesLoading = false;
        state.publicResources = action.payload.content;
        state.publicResourcesError = null;
      },
    );
    builder.addCase(fetchPublicWebinarResources.rejected, (state, action) => {
      state.publicResourcesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicResourcesError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public resources';
    });

    builder.addCase(fetchPublicResource.pending, (state) => {
      state.publicResourceLoading = true;
      state.publicResourceError = null;
    });
    builder.addCase(
      fetchPublicResource.fulfilled,
      (state, action: PayloadAction<WebinarResource>) => {
        state.publicResourceLoading = false;
        state.publicResource = action.payload;
        state.publicResourceError = null;
      },
    );
    builder.addCase(fetchPublicResource.rejected, (state, action) => {
      state.publicResourceLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicResourceError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public resource';
    });

    builder.addCase(fetchPublicWorkspaceSocialMediaLinks.pending, (state) => {
      state.publicWorkspaceSocialMediaLinksLoading = true;
      state.publicWorkspaceSocialMediaLinksError = null;
    });
    builder.addCase(
      fetchPublicWorkspaceSocialMediaLinks.fulfilled,
      (state, action: PayloadAction<WorkspaceSocialMediaLink[]>) => {
        state.publicWorkspaceSocialMediaLinksLoading = false;
        state.publicWorkspaceSocialMediaLinks = action.payload;
        state.publicWorkspaceSocialMediaLinksError = null;
      },
    );
    builder.addCase(fetchPublicWorkspaceSocialMediaLinks.rejected, (state, action) => {
      state.publicWorkspaceSocialMediaLinksLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWorkspaceSocialMediaLinksError =
        payload?.message ??
        action.error.message ??
        'Failed to fetch public workspace social media links';
    });

    // ---- Notifications par offre (config.288) ----
    builder.addCase(fetchOfferPurchaseNotificationConfig.pending, (state, action) => {
      state.offerPurchaseNotificationConfigLoading[action.meta.arg.offerId] = true;
      state.offerPurchaseNotificationConfigError[action.meta.arg.offerId] = null;
    });
    builder.addCase(fetchOfferPurchaseNotificationConfig.fulfilled, (state, action) => {
      const offerId = action.meta.arg.offerId;
      state.offerPurchaseNotificationConfigLoading[offerId] = false;
      state.offerPurchaseNotificationConfigs[offerId] = action.payload;
      state.offerPurchaseNotificationConfigError[offerId] = null;
    });
    builder.addCase(fetchOfferPurchaseNotificationConfig.rejected, (state, action) => {
      const offerId = action.meta.arg.offerId;
      state.offerPurchaseNotificationConfigLoading[offerId] = false;
      const payload = action.payload as { message?: string } | undefined;
      state.offerPurchaseNotificationConfigError[offerId] =
        payload?.message ?? action.error.message ?? 'Failed to fetch offer notification config';
    });

    builder.addCase(updateOfferPurchaseNotificationConfig.pending, (state, action) => {
      state.updateOfferPurchaseNotificationConfigLoading[action.meta.arg.offerId] = true;
      state.updateOfferPurchaseNotificationConfigError[action.meta.arg.offerId] = null;
    });
    builder.addCase(updateOfferPurchaseNotificationConfig.fulfilled, (state, action) => {
      state.updateOfferPurchaseNotificationConfigLoading[action.meta.arg.offerId] = false;
      state.updateOfferPurchaseNotificationConfigError[action.meta.arg.offerId] = null;
    });
    builder.addCase(updateOfferPurchaseNotificationConfig.rejected, (state, action) => {
      const offerId = action.meta.arg.offerId;
      state.updateOfferPurchaseNotificationConfigLoading[offerId] = false;
      const payload = action.payload as { message?: string } | undefined;
      state.updateOfferPurchaseNotificationConfigError[offerId] =
        payload?.message ?? action.error.message ?? 'Failed to update offer notification config';
    });

    builder.addCase(submitPublicEvaluation.pending, (state) => {
      state.submitPublicEvaluationLoading = true;
      state.submitPublicEvaluationError = null;
    });
    builder.addCase(submitPublicEvaluation.fulfilled, (state) => {
      state.submitPublicEvaluationLoading = false;
      state.submitPublicEvaluationError = null;
    });
    builder.addCase(submitPublicEvaluation.rejected, (state, action) => {
      state.submitPublicEvaluationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.submitPublicEvaluationError =
        payload?.message ?? action.error.message ?? 'Failed to submit public evaluation';
    });

    builder.addCase(fetchEventHistory.pending, (state) => {
      state.eventHistoryLoading = true;
      state.eventHistoryError = null;
    });
    builder.addCase(
      fetchEventHistory.fulfilled,
      (state, action: PayloadAction<WebinarEventHistoryDto>) => {
        state.eventHistoryLoading = false;
        state.eventHistory = action.payload;
        state.eventHistoryError = null;
      },
    );
    builder.addCase(fetchEventHistory.rejected, (state, action) => {
      state.eventHistoryLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.eventHistoryError =
        payload?.message ?? action.error.message ?? 'Failed to fetch event history';
    });

    builder.addCase(fetchPresenceStatus.pending, (state) => {
      state.presenceStatusLoading = true;
      state.presenceStatusError = null;
    });
    builder.addCase(
      fetchPresenceStatus.fulfilled,
      (state, action: PayloadAction<WebinarPresenceStatusDto>) => {
        state.presenceStatusLoading = false;
        state.presenceStatus = action.payload;
        state.presenceStatusError = null;
      },
    );
    builder.addCase(fetchPresenceStatus.rejected, (state, action) => {
      state.presenceStatusLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.presenceStatusError =
        payload?.message ?? action.error.message ?? 'Failed to fetch presence status';
    });

    builder.addCase(switchWorkspace, () => initialState);

    builder.addMatcher(
      isAnyOf(fetchPublicEvergreen.pending, refreshEvergreenChatMode.pending),
      (state, action) => {
        state.evergreenChatModeSync = chatModeRequestStarted(
          state.evergreenChatModeSync,
          action.meta.requestId,
        );
      },
    );
    builder.addMatcher(
      isAnyOf(fetchPublicEvergreen.fulfilled, refreshEvergreenChatMode.fulfilled),
      (state, action) => {
        state.evergreenChatModeSync = chatModeRequestFulfilled(
          state.evergreenChatModeSync,
          action.meta.requestId,
          action.meta.arg.sessionIdentifier,
          action.payload.attendeeChatMode,
        );
      },
    );

    builder.addMatcher(
      (action) => action.type === 'webinar/deleteWebinarSlide/fulfilled',
      (state, action: { payload: number }) => {
        const slideId = action.payload;
        state.adminResourceStates = state.adminResourceStates.map((s) =>
          s.resourceType === 'WEBINAR_SLIDE' && s.resourceId === slideId
            ? { ...s, live: false }
            : s,
        );
        state.publicResourceStates = state.publicResourceStates.map((s) =>
          s.resourceType === 'WEBINAR_SLIDE' && s.resourceId === slideId
            ? { ...s, live: false }
            : s,
        );
        if (state.publicSlide?.id === slideId) {
          state.publicSlide = null;
        }
      },
    );
  },
});

export const {
  setParticipantTrackPublications,
  stageInviteEventReceived,
  stageInvitesReconciled,
  setStreamStartStatus,
  applyEvergreenChatModeChange,
  clearPublicOffer,
  openOfferEmbed,
  closeOfferEmbed,
  clearPublicSurvey,
  clearPublicQuiz,
  hidePublicOffer,
  hidePublicSurvey,
  hidePublicQuiz,
  clearPublicResource,
  clearPublicSlide,
  hydrateAttendeeInfoFromSubscriptionJwt,
  setYoutubeClosePending,
  reset: resetLive,
} = slice.actions;

export default slice.reducer;
